import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  Star,
  Calendar,
  Award,
  Camera,
  Save,
  AlertCircle,
  BookOpen,
  Users,
  TrendingUp
} from 'lucide-react';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';
import { toast } from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';

interface InstructorData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  bio?: string;
  specialties: string[];
  experience: number;
  certifications: string[];
  avatar?: string;
  rating: number;
  totalClasses: number;
  totalStudents: number;
  hireDate: string;
}

interface ClassType {
  id: string;
  name: string;
  color: string;
}

const InstructorProfile: React.FC = () => {
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instructorData, setInstructorData] = useState<InstructorData | null>(null);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    bio: ''
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [ratings, setRatings] = useState<{ average: number; count: number }>({ average: 0, count: 0 });

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorData();
    }
  }, [businessId, currentUser]);

  const loadInstructorData = async () => {
    if (!businessId || !currentUser) return;

    try {
      setLoading(true);

      // Buscar instructor
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const snapshot = await getDocs(instructorsQuery);

      if (snapshot.empty) {
        setLoading(false);
        return;
      }

      const instructorDoc = snapshot.docs[0];
      const data = {
        id: instructorDoc.id,
        ...instructorDoc.data()
      } as InstructorData;

      setInstructorData(data);
      setFormData({
        phone: data.phone || '',
        bio: data.bio || ''
      });
      setAvatarPreview(data.avatar || '');

      // Usar la valoración guardada en el instructor si existe
      if (data.rating && data.rating > 0) {
        setRatings({
          average: data.rating,
          count: (data as any).totalRatings || 0
        });
      }

      // Cargar tipos de clase
      const classTypesQuery = query(
        collection(db, 'classTypes'),
        where('businessId', '==', businessId)
      );
      const classTypesSnapshot = await getDocs(classTypesQuery);
      setClassTypes(classTypesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ClassType[]);

      // Cargar valoraciones desde la colección ratings (si existen)
      try {
        const ratingsQuery = query(
          collection(db, 'ratings'),
          where('targetType', '==', 'instructor'),
          where('targetId', '==', instructorDoc.id)
        );
        const ratingsSnapshot = await getDocs(ratingsQuery);

        if (!ratingsSnapshot.empty) {
          const ratingValues = ratingsSnapshot.docs.map(doc => doc.data().rating as number);
          const avg = ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length;
          setRatings({ average: avg, count: ratingValues.length });
        }
      } catch (error) {
        console.log('Usando valoración del documento de instructor');
      }

    } catch (error) {
      console.error('Error cargando perfil:', error);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!instructorData || !currentUser) return;

    try {
      setSaving(true);

      let avatarUrl = instructorData.avatar;

      // Subir nueva foto si existe
      if (avatarFile) {
        console.log('🔄 Comprimiendo imagen de perfil...');
        const compressedFile = await compressImage(avatarFile);

        const path = `instructors/${businessId}/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressedFile);
        avatarUrl = await getDownloadURL(storageRef);
      }

      // Actualizar instructor
      await updateDoc(doc(db, 'instructors', instructorData.id), {
        phone: formData.phone,
        bio: formData.bio,
        avatar: avatarUrl,
        updatedAt: serverTimestamp()
      });

      // También actualizar la foto en el documento del usuario para el header
      if (avatarUrl) {
        await updateDoc(doc(db, 'users', currentUser.id), {
          profilePhotoUrl: avatarUrl,
          updatedAt: serverTimestamp()
        });
      }

      setInstructorData(prev => prev ? {
        ...prev,
        phone: formData.phone,
        bio: formData.bio,
        avatar: avatarUrl
      } : null);

      setEditMode(false);
      setAvatarFile(null);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error guardando perfil:', error);
      toast.error('Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No especificado';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!instructorData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Cuenta no vinculada</h2>
          <p className="text-gray-400">
            Tu cuenta no está vinculada a un perfil de instructor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Mi Perfil</h1>
          <p className="text-gray-400">Información de tu perfil de instructor</p>
        </div>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)}>
            Editar
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => {
              setEditMode(false);
              setFormData({
                phone: instructorData.phone || '',
                bio: instructorData.bio || ''
              });
              setAvatarFile(null);
              setAvatarPreview(instructorData.avatar || '');
            }}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        )}
      </div>

      {/* Foto y nombre */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt={instructorData.firstName}
                className="w-28 h-28 rounded-full object-cover border-4 border-primary-500"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gray-700 flex items-center justify-center border-4 border-gray-600">
                <User className="w-12 h-12 text-gray-500" />
              </div>
            )}
            {editMode && (
              <label className="absolute bottom-0 right-0 p-2 bg-primary-500 rounded-full cursor-pointer hover:bg-primary-600 transition-colors">
                <Camera className="w-4 h-4 text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <div className="text-center sm:text-left flex-1">
            <h2 className="text-2xl font-bold text-white">
              {instructorData.firstName} {instructorData.lastName}
            </h2>
            <p className="text-gray-400">{instructorData.email}</p>
            <div className="flex items-center justify-center sm:justify-start gap-4 mt-3">
              <div className="flex items-center gap-1 text-amber-400">
                <Star className="w-5 h-5 fill-current" />
                <span className="font-semibold">{ratings.average.toFixed(1)}</span>
                <span className="text-gray-500 text-sm">({ratings.count} valoraciones)</span>
              </div>
              <div className="flex items-center gap-1 text-gray-400">
                <Award className="w-4 h-4" />
                <span>{instructorData.experience || 0} años exp.</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Estadísticas */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <BookOpen className="w-6 h-6 text-primary-400 mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{instructorData.totalClasses || 0}</div>
          <div className="text-sm text-gray-500">Clases totales</div>
        </Card>
        <Card className="p-4 text-center">
          <Users className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{instructorData.totalStudents || 0}</div>
          <div className="text-sm text-gray-500">Estudiantes</div>
        </Card>
        <Card className="p-4 text-center">
          <Calendar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <div className="text-lg font-bold text-white">{formatDate(instructorData.hireDate)}</div>
          <div className="text-sm text-gray-500">Desde</div>
        </Card>
      </div>

      {/* Información de contacto */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Información de Contacto</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Email</label>
            <div className="flex items-center gap-2 text-white">
              <Mail className="w-4 h-4 text-gray-500" />
              {instructorData.email}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Teléfono</label>
            {editMode ? (
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+34 600 000 000"
              />
            ) : (
              <div className="flex items-center gap-2 text-white">
                <Phone className="w-4 h-4 text-gray-500" />
                {instructorData.phone || 'No especificado'}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Biografía */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Biografía</h3>
        {editMode ? (
          <textarea
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
            placeholder="Escribe una breve descripción sobre ti..."
            rows={4}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        ) : (
          <p className="text-gray-300">
            {instructorData.bio || 'No hay biografía disponible'}
          </p>
        )}
      </Card>

      {/* Especialidades */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Especialidades</h3>
        {instructorData.specialties && instructorData.specialties.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {instructorData.specialties.map((specialty, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-primary-500/20 text-primary-300 rounded-full text-sm font-medium border border-primary-500/30"
              >
                {specialty}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No hay especialidades registradas</p>
        )}
      </Card>

      {/* Certificaciones */}
      {instructorData.certifications && instructorData.certifications.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Certificaciones</h3>
          <div className="space-y-2">
            {instructorData.certifications.map((cert, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-300">
                <Award className="w-4 h-4 text-amber-400" />
                {cert}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Nota informativa */}
      <Card className="p-4 bg-blue-500/10 border-blue-500/30">
        <p className="text-sm text-blue-300 text-center">
          💡 Para modificar especialidades, certificaciones u otra información, contacta con el administrador.
        </p>
      </Card>
    </div>
  );
};

export default InstructorProfile;

